import React, { useState } from 'react';
import axios from 'axios';
import { Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPLOYEES = ['1', '2-5', '6-20', '21-50', '50+'];
const INTERESTS = [
  'Facturación + Verifactu',
  'OCR de gastos',
  'Conciliación bancaria',
  'Inventario / Artículos',
  'Suscripciones + SEPA',
  'Time tracking + Rentabilidad',
  'Predicción fiscal + Asesor IA',
  'Multi-empresa (Gestoría)',
  'Otro',
];

const initial = {
  name: '', company: '', email: '', phone: '',
  employees: '', sector: '', interest: '',
  message: '', consent: false, website: '',
};

const DemoRequestForm = ({ source = 'landing' }) => {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!data.name || data.name.trim().length < 2) { setError('Indica tu nombre'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) { setError('Email inválido'); return; }
    if (!data.consent) { setError('Debes aceptar la política de privacidad'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/demo-requests`, { ...data, source });
      setDone(true);
      setData(initial);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-3xl border border-emerald-100 shadow-2xl shadow-stone-200/40 p-10 text-center" data-testid="demo-form-success">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
          <Check className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
        </div>
        <h3 className="text-2xl font-extrabold tracking-tight text-stone-950 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          ¡Solicitud recibida!
        </h3>
        <p className="text-stone-500 mb-6">Un especialista de Facturcal te contactará en menos de 24h laborables.</p>
        <button onClick={() => setDone(false)} className="text-sm font-semibold text-[#FF6B4A] hover:text-[#E05A3E] transition-colors" data-testid="demo-form-reset">
          Enviar otra solicitud
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-200 shadow-2xl shadow-stone-200/50 p-7 md:p-9" data-testid="demo-form">
      <div className="inline-flex items-center gap-2 bg-orange-50 text-[#FF6B4A] px-3 py-1 rounded-full text-[11px] font-semibold mb-5 border border-orange-100">
        <Sparkles className="w-3 h-3" /> Demo personalizada en 30 min
      </div>

      {/* Honeypot: invisible to humans, bots will fill it */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={data.website}
        onChange={(e) => set('website', e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        aria-hidden="true"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nombre" required>
          <input value={data.name} onChange={(e) => set('name', e.target.value)} className={inputCls} placeholder="Tu nombre" data-testid="demo-name" />
        </Field>
        <Field label="Empresa">
          <input value={data.company} onChange={(e) => set('company', e.target.value)} className={inputCls} placeholder="Tu empresa" data-testid="demo-company" />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Field label="Email" required>
          <input type="email" value={data.email} onChange={(e) => set('email', e.target.value)} className={inputCls} placeholder="tucorreo@empresa.com" data-testid="demo-email" />
        </Field>
        <Field label="Teléfono">
          <input value={data.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} placeholder="+34 ___" data-testid="demo-phone" />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Field label="Nº empleados">
          <select value={data.employees} onChange={(e) => set('employees', e.target.value)} className={inputCls} data-testid="demo-employees">
            <option value="">Selecciona…</option>
            {EMPLOYEES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Sector">
          <input value={data.sector} onChange={(e) => set('sector', e.target.value)} className={inputCls} placeholder="Construcción, abogacía…" data-testid="demo-sector" />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Módulo de mayor interés">
          <select value={data.interest} onChange={(e) => set('interest', e.target.value)} className={inputCls} data-testid="demo-interest">
            <option value="">Sin preferencia / quiero ver todo</option>
            {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Cuéntanos algo más (opcional)">
          <textarea value={data.message} onChange={(e) => set('message', e.target.value)} className={`${inputCls} resize-none`} rows={3} placeholder="Volumen de facturas, dudas concretas, sistema actual…" data-testid="demo-message" />
        </Field>
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input
          type="checkbox"
          checked={data.consent}
          onChange={(e) => set('consent', e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#FF6B4A]"
          data-testid="demo-consent"
        />
        <span className="text-xs text-stone-500 leading-relaxed">
          He leído y acepto la <a href="/privacidad" target="_blank" className="text-[#FF6B4A] hover:underline">política de privacidad</a>. Mis datos se usarán únicamente para contactarme sobre la demo solicitada.
        </span>
      </label>

      {error && (
        <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 text-sm text-red-700" data-testid="demo-form-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FF6B4A] text-white py-3.5 rounded-full text-sm font-semibold hover:bg-[#E05A3E] transition-all shadow-lg shadow-orange-200/50 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="demo-submit"
      >
        {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</>) : 'Solicitar mi demo'}
      </button>

      <p className="text-[11px] text-stone-400 text-center mt-3">Sin compromiso. Te contactaremos en menos de 24h.</p>
    </form>
  );
};

const inputCls = "w-full px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:border-[#FF6B4A] focus:bg-white transition-all placeholder:text-stone-400";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-stone-600 mb-1.5 block">{label}{required && <span className="text-[#FF6B4A] ml-0.5">*</span>}</span>
    {children}
  </label>
);

export default DemoRequestForm;
